import type { HostConfig } from '../host'

/**
 * o-cam.app — the consolidated app, and the first host that is not a tenant.
 *
 * Every other entry in `HOSTS` is a white-label of someone's API: the
 * hostname picks a brand, an upstream and a login page, and the app wears
 * that tenant's clothes. This one has no tenant. Where a film can go is
 * installed at runtime with a code (`src/config/catalogue.ts`), several at a
 * time, so the three fields that used to be the point of a host config —
 * `webOrigin`, `loginPath`, `capabilities` — have nothing to say here.
 *
 * That is what `standalone` marks, and it changes two things in the worker:
 * page loads are not gated behind anyone's session, and `/api/*` is not
 * proxied to a single upstream. See `worker.ts`.
 *
 * ## What does not work here yet, and why it is not a bug
 *
 * **OKB and Console cannot be published to from this domain.** Their auth is
 * `host-session`: the recorder has always been a SUBDOMAIN of the host
 * (`cam.o-kb.com`, `cam.console.openside.com`), so the browser attaches that
 * host's session cookie and the worker proxies it onward. `o-cam.app` is a
 * different registrable domain. No cookie attribute can send
 * `console.openside.com`'s session to it — that is the web's origin model,
 * not a configuration we have got wrong, and `.openside.com` would not help
 * because the cookie would then also reach the public asset host.
 *
 * So those two destinations need an auth that travels: an API token, or
 * OAuth, issued by those apps. Until then they stay on their own `cam.`
 * hostnames, which is exactly what the migration ordering in
 * `docs/consolidating.md` §6 assumes — the old origins stay up until everyone
 * has exported. A `host-session` destination installed here says so rather
 * than failing at its first API call.
 */
export const ocamHost: HostConfig = {
  key: 'ocam',
  hostnames: [
    'o-cam.app',
    'www.o-cam.app',
    /*
     * Local dev. These used to resolve to the OKB config, which is gone — and
     * resolution is exact-match with no fallback, so without them `pnpm dev`
     * and `wrangler dev` serve a 404 and the client falls back to the
     * unbranded `genericHost`.
     *
     * Dev is standalone too, which is now simply accurate: there is no tenant
     * API left to proxy to.
     */
    'localhost',
    '127.0.0.1',
  ],

  /*
   * There is no upstream. Both of these exist only because `HostConfig`
   * requires them, and `standalone` is what actually decides behaviour — the
   * worker never proxies to this origin and never links to this login.
   *
   * `.invalid` is reserved by RFC 2606 and guaranteed not to resolve, which
   * is the point: if a code path ever does reach for an upstream here, it
   * fails immediately and loudly rather than quietly talking to a real host.
   */
  webOrigin: 'https://o-cam.invalid',
  loginPath: '/',
  standalone: true,

  brand: {
    productName: 'o-cam Studio',
    orgName: 'o-cam',
    tagline: 'Record, dictate or write — then publish wherever you like.',
    description:
      'o-cam — record clips on your phone and publish them wherever you have installed.',
    /*
     * A lens with a record dot: owned by nobody, legible at 24px, and
     * deliberately not a tenant's mark — this host has no tenant. Bounds are
     * 0–110 to match the shell's -14 -14 138 138 viewBox, the box every
     * host's mark is drawn in.
     */
    markPath:
      'M55,0 A55,55 0 1,0 55,110 A55,55 0 1,0 55,0 Z M55,18 A37,37 0 1,1 55,92 A37,37 0 1,1 55,18 Z M55,37 A18,18 0 1,0 55,73 A18,18 0 1,0 55,37 Z',
    faviconPath: '/brand/ocam/favicon.svg',
    themeColorDark: '#141414',
    themeColorLight: '#FFFFFF',
    // No palette: global.css's own neutral slate is this app's palette. It is
    // nobody's brand any more, which is the whole point of the consolidation.
  },

  /*
   * All false, and not a stub. A service is now a property of what you have
   * INSTALLED, not of where the page was served from — `availableServices()`
   * unions them across installed destinations. A standalone host offering
   * transcription from here would be claiming something no destination
   * promised.
   */
  capabilities: {
    transcription: false,
    metadataGeneration: false,
    notesAssistant: false,
  },

  /*
   * Nouns for a two-level publish target, which this host does not have —
   * each installed destination brings its own `taxonomy`. Present because
   * `HostConfig` requires it; nothing on this host reads it.
   */
  destination: {
    level1: { label: 'Destination', plural: 'Destinations' },
    level2: { label: 'Folder', plural: 'Folders' },
  },

  storage: {
    /*
     * A fresh database, and necessarily so: IndexedDB is per ORIGIN, so
     * nothing from me.o-kb.com or cam.console.openside.com is visible here
     * whatever this is called. Projects arrive as backup files — which is why
     * "Export everything" shipped to the old deploys first.
     *
     * Frozen from the first deploy, like every other host's: renaming it
     * later orphans every project on every device.
     */
    databaseName: 'o-cam',
    keyPrefix: 'oCam.',
    backupExtension: '.ocamvideo',
  },
}
